import WidgetType = ToroEnums.WidgetType;
import WizardIntegration = ToroEnums.WizardIntegration;

import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { finalize, switchMap, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AssetTrackingManagerService } from '../../../../../api/asset-tracking/asset-tracking-manager.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { CalAmpDevice } from '../../../../../api/cal-amp/models/cal-amp-device.model';
import { DashUserInfo } from '../../../../../api/dash-user/models/dash-user-info.model';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { ToroEnums } from '../../../../../common/enumerations/toro.enums';
import { WizardSettings } from '../../models/wizard-settings.model';

@UntilDestroy()
@Component({
    selector: 'toro-asset-tracking-integration',
    templateUrl: './asset-tracking-integration.component.html',
    styleUrls: ['./asset-tracking-integration.component.less']
})
export class AssetTrackingIntegrationComponent implements OnInit, OnDestroy {
    @HostBinding('class') class = 'wiz-integration-component toro-asset-tracking-integration';

    @Output() canConnect = new EventEmitter<boolean>();
    @Output() configured = new EventEmitter<WizardIntegration>();
    @Output() settingsChange = new EventEmitter<WizardSettings>();

    @Input() settings: WizardSettings;

    readonly stepsCount = 1;
    private elements: any;

    isBusy = false;
    showError = false;
    spinnerText = 'CASE_SENSITIVE.VERIFYING_ACCOUNT_ID'
    accountId: number;

    // =========================================================================================================================================================
    // C'tor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(private assetTrackingManager: AssetTrackingManagerService,
                private broadcastService: BroadcastService,
                private dashUserManager: DashUserManagerService,
    ) { }

    ngOnInit(): void {
        this.broadcastService.setupDoConnect
            .pipe(untilDestroyed(this))
            .subscribe(() => this.verifyConfiguration());

        // We want to modify the behavior of the p-inputNumber component to allow us to assess the controls
        // value after each key press. To do that, we will search the dom for our unique container component
        // and then find out "common" p-inputNumber class element. There will only be one in our container.
        // Once we have our element, we will assign the keyup event handler.
        const container = document.getElementsByClassName('wic-cal-amp-info');
        this.elements = container[0].getElementsByClassName('ui-inputnumber-input');

        if (this.elements.length != 1) throw new Error('There should be one and only one p-inputNumber element here.');
        this.elements[0].addEventListener('keyup', this.keyUp.bind(this))

        if (this.settings && this.settings.assetTrackingAccountId != null) {
            this.accountId = this.settings.assetTrackingAccountId;
            this.canConnect.next(true);
        }
    }

    ngOnDestroy() {
        this.elements[0].removeEventListener('keyup', this.keyUp.bind(this))
        this.canConnect.emit(false);
    }

    keyUp(event) {
        this.canConnect.emit(event.target.value.length > 0);
    }

    onPastOrCutAccountId(event: ClipboardEvent) {
        const pastedText = event?.clipboardData?.getData('text');
        this.canConnect.emit(pastedText && pastedText.length > 0);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private verifyConfiguration() {
        this.showError = false;
        this.isBusy = true;

        this.dashUserManager.getDashUserInfo()
            .pipe(
                take(1),
                switchMap((dashUserInfo: DashUserInfo) => {
                    return this.assetTrackingManager.getDeviceList(this.accountId, false).pipe(take(1))
                }),
                finalize(() => this.isBusy = false)
            )
            .subscribe({
                next: (devices: CalAmpDevice[]) => {
                    this.broadcastService.setupToggleWidgetState.next({ type: WidgetType.EquipmentTracking, isEnabled: true });
                    this.settings.assetTrackingAccountId = this.accountId;
                    this.settingsChange.next(this.settings);
                    // NOTE: If we bring back this wizard step, we will need to uncomment this line.
                    // this.configured.next(WizardIntegration.AssetTracking);
                },
                error: () => {
                    this.showError = true;
                }
            })
    }

}
